import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';

/**
 * One section of the filter modal / refine sheet. Named separately from the
 * configs below because the legacy products page and the browse redesign list
 * the same sections in a different order, and a section is defined once.
 */
type ModalSection = FiltersConfig['modalFilters'][number];

const COLOUR_SECTION: ModalSection = {
    title: 'Colour',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'color',
            filterKey: 'colors',
        },
    ],
};

const MATERIAL_SECTION: ModalSection = {
    title: 'Material',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'searchable',
            aggregationProperty: 'aggProductMaterials',
            filterKey: 'materials',
            searchInputPlaceholder: 'Search by Name',
            allowMultipleValues: true,
            allowAlphabeticalGrouping: true,
            columns: 1,
        },
    ],
};

const SIZE_SECTION: ModalSection = {
    title: 'Size',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'simpleRange',
            filterKey: 'lengthRange',
            label: 'Length',
            minPlaceholder: '0',
            maxPlaceholder: 'Max',
            unit: 'mm',
        },
        {
            type: 'simpleRange',
            filterKey: 'widthRange',
            label: 'Width',
            minPlaceholder: '0',
            maxPlaceholder: 'Max',
            unit: 'mm',
        },
        {
            type: 'simpleRange',
            filterKey: 'heightRange',
            label: 'Height',
            minPlaceholder: '0',
            maxPlaceholder: 'Max',
            unit: 'mm',
        },
        {
            type: 'simpleRange',
            filterKey: 'diameterRange',
            label: 'Diameter',
            minPlaceholder: '0',
            maxPlaceholder: 'Max',
            unit: 'mm',
        },
        {
            type: 'simpleRange',
            filterKey: 'weightRange',
            label: 'Weight',
            minPlaceholder: '0',
            maxPlaceholder: 'Max',
            unit: 'kg',
        },
    ],
};

const DOWNLOADS_SECTION: ModalSection = {
    title: 'Downloads',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'default',
            filterKey: 'downloads',
        },
    ],
};

const SUPPLIERS_SECTION: ModalSection = {
    title: 'Suppliers',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'searchable',
            aggregationProperty: 'aggProfessionals',
            filterKey: 'professionals',
            searchInputPlaceholder: 'Search by Name',
            allowMultipleValues: true,
            allowAlphabeticalGrouping: true,
            columns: 1,
        },
    ],
};

const BRAND_SECTION: ModalSection = {
    title: 'Brand',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'searchable',
            aggregationProperty: 'aggBrands',
            filterKey: 'brands',
            searchInputPlaceholder: 'Search by Brand',
            allowMultipleValues: true,
            allowAlphabeticalGrouping: true,
            columns: 1,
        },
    ],
};

const DESIGNER_SECTION: ModalSection = {
    title: 'Designer',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'searchable',
            aggregationProperty: 'aggDesigners',
            filterKey: 'designers',
            searchInputPlaceholder: 'Search by Name',
            allowMultipleValues: true,
            allowAlphabeticalGrouping: true,
            columns: 1,
        },
    ],
};

const AVAILABILITY_SECTION: ModalSection = {
    title: 'Availability',
    collapsable: true,
    collapsedByDefault: true,
    filters: [
        {
            type: 'default',
            filterKey: 'isInStock',
            label: 'In stock',
        },
        {
            type: 'default',
            filterKey: 'isDeliveryAvailable',
            label: 'Delivery available',
        },
        {
            type: 'default',
            filterKey: 'isSampleAvailable',
            label: 'Samples available',
        },
    ],
};

const PRICING_SECTION: ModalSection = {
    title: 'Pricing',
    collapsable: true,
    collapsedByDefault: false,
    filters: [
        {
            type: 'default',
            filterKey: 'isPurchasable',
            label: 'Shop',
        },
        {
            type: 'default',
            filterKey: 'isOnSale',
            label: 'On sale (Discount)',
        },
        {
            type: 'price',
            filterKey: 'priceRange',
            label: 'Price range',
        },
    ],
};

const STAFF_ADMIN_SECTION: ModalSection = {
    title: 'Staff admin filters',
    collapsable: true,
    collapsedByDefault: true,
    adminOnly: true,
    filters: [
        {
            type: 'default',
            filterKey: 'invalidLink',
            label: 'Invalid Link',
        },
        {
            type: 'default',
            filterKey: 'disabled',
            label: 'Disabled',
        },
        {
            type: 'default',
            filterKey: 'profileOnly',
            label: 'Profile Only',
        },
    ],
};

/**
Product filters configuration
Note: The order of filters in this configuration is important because it determines the sequence in which filters are applied and displayed.
This can affect both the user interface and the logic that processes these filters.
 **/
export const PRODUCT_FILTERS_CONFIG: FiltersConfig = {
    resultName: 'Product',
    barFilters: [
        // Order: Colour → Material → Size
        // Staff admin filters are only shown in the modal/drawer (see modalFilters)
        {
            title: 'Colour',
            hideOnSuppliers: true,
            minContentWidth: 20,
            filters: [
                {
                    type: 'color',
                    filterKey: 'colors',
                },
            ],
        },
        {
            title: 'Material',
            hideOnSuppliers: true,
            minContentWidth: 20,
            filters: [
                {
                    type: 'searchable',
                    aggregationProperty: 'aggProductMaterials',
                    filterKey: 'materials',
                    searchInputPlaceholder: 'Search by Material',
                    allowMultipleValues: true,
                    allowAlphabeticalGrouping: true,
                },
            ],
        },
        {
            title: 'Size',
            hideOnSuppliers: true,
            minContentWidth: 15,
            popoverBackground: 'none',
            popoverClassName:
                'before:backdrop-hack before:rounded-4 before:backdrop-blur-[1rem] before:bg-[rgba(224,224,224,0.7)]',
            filters: [
                {
                    type: 'simpleRange',
                    filterKey: 'lengthRange',
                    label: 'Length',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'widthRange',
                    label: 'Width',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'heightRange',
                    label: 'Height',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'diameterRange',
                    label: 'Diameter',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'mm',
                },
                {
                    type: 'simpleRange',
                    filterKey: 'weightRange',
                    label: 'Weight',
                    minPlaceholder: '0',
                    maxPlaceholder: 'Max',
                    unit: 'kg',
                },
            ],
        },
    ],
    modalFilters: [
        PRICING_SECTION,
        AVAILABILITY_SECTION,
        DOWNLOADS_SECTION,
        COLOUR_SECTION,
        MATERIAL_SECTION,
        BRAND_SECTION,
        DESIGNER_SECTION,
        SIZE_SECTION,
        SUPPLIERS_SECTION,
        STAFF_ADMIN_SECTION,
    ],
};

/**
 * Modal section order for the 2026 browse redesign: the visual facets lead and
 * the commercial ones close, and Pricing arrives collapsed. The legacy products
 * page keeps its own order above — it shares every section, only the sequence
 * and Pricing's initial state differ.
 */
export const PRODUCT_FILTERS_CONFIG_REVAMP: FiltersConfig = {
    ...PRODUCT_FILTERS_CONFIG,
    modalFilters: [
        COLOUR_SECTION,
        MATERIAL_SECTION,
        SIZE_SECTION,
        DOWNLOADS_SECTION,
        SUPPLIERS_SECTION,
        BRAND_SECTION,
        DESIGNER_SECTION,
        AVAILABILITY_SECTION,
        PRICING_SECTION,
        STAFF_ADMIN_SECTION,
    ].map((section) =>
        section === PRICING_SECTION
            ? { ...section, collapsedByDefault: true }
            : section,
    ),
};
